import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Landing from './components/Landing';
import Login from './components/Login';
import Register from './components/Register';
import Wallet from './components/Wallet';
import Teams from './components/Teams';
import JoinSession from './components/JoinSession';
import CreateMatch from './components/CreateMatch';
import Match from './components/Match';
import Matches from './components/Matches';
import Profile from './components/Profile';
import Leaderboard from './components/Leaderboard';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import DeviceManagement from './components/admin/DeviceManagement';
import VenueManagement from './components/admin/VenueManagement';
import PendingMatchNotifications from './components/PendingMatchNotifications';

interface DecodedToken {
  id: string;
  email: string;
  role: string;
}

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  let isAdmin = false;
  if (token) {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      isAdmin = decoded.role === 'admin';
    } catch (error) {
    }
  }

  if (location.pathname === '/') {
    return null;
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <nav className="bg-gradient-to-r from-primary-800 via-secondary-800 to-accent-800 text-white shadow-lg sticky top-0 z-50 backdrop-blur-sm bg-opacity-95">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-2 text-2xl font-bold hover:scale-105 transition-transform">
            <span className="text-3xl">⚽</span>
            <span className="bg-gradient-to-r from-primary-200 to-secondary-200 bg-clip-text text-transparent">Goalie</span>
          </Link>

          <ul className="hidden md:flex space-x-1 lg:space-x-2">
            {!isLoggedIn ? (
              <>
                <li>
                  <Link to="/login" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    Přihlášení
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg hover:shadow-glow transition-all duration-300 hover:scale-105 inline-block font-semibold">
                    Registrace
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link to="/wallet" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    💰 Peněženka
                  </Link>
                </li>
                <li>
                  <Link to="/teams" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    👥 Týmy
                  </Link>
                </li>
                <li>
                  <Link to="/join-session" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    🎮 Hrát
                  </Link>
                </li>
                <li>
                  <Link to="/matches" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    📅 Zápasy
                  </Link>
                </li>
                <li>
                  <Link to="/profile" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    👤 Profil
                  </Link>
                </li>
                <li>
                  <Link to="/leaderboard" className="px-4 py-2 rounded-lg hover:bg-white/10 transition-all duration-300 hover:scale-105 inline-block">
                    🏆 Žebříček
                  </Link>
                </li>
                {isAdmin && (
                  <li>
                    <Link to="/admin" className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg hover:shadow-glow transition-all duration-300 hover:scale-105 inline-block font-semibold">
                      🛠️ Administrace
                    </Link>
                  </li>
                )}
                <li>
                  <button onClick={handleLogout} className="px-4 py-2 bg-red-500/80 rounded-lg hover:bg-red-600 transition-all duration-300 hover:scale-105 font-semibold">
                    Odhlásit se
                  </button>
                </li>
              </>
            )}
          </ul>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden pb-4 animate-slide-up">
            <ul className="space-y-2 text-left">
              {!isLoggedIn ? (
                <>
                  <li>
                    <Link to="/login" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Přihlášení
                    </Link>
                  </li>
                  <li>
                    <Link to="/register" className="block px-4 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg font-semibold text-left" onClick={() => setIsMenuOpen(false)}>
                      Registrace
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link to="/wallet" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Peněženka
                    </Link>
                  </li>
                  <li>
                    <Link to="/teams" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Týmy
                    </Link>
                  </li>
                  <li>
                    <Link to="/join-session" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Hrát
                    </Link>
                  </li>
                  <li>
                    <Link to="/matches" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Zápasy
                    </Link>
                  </li>
                  <li>
                    <Link to="/profile" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Profil
                    </Link>
                  </li>
                  <li>
                    <Link to="/leaderboard" className="block px-4 py-3 rounded-lg hover:bg-white/10 transition-colors text-left" onClick={() => setIsMenuOpen(false)}>
                      Žebříček
                    </Link>
                  </li>
                  {isAdmin && (
                  <li>
                    <Link to="/admin" className="block px-4 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg font-semibold text-left" onClick={() => setIsMenuOpen(false)}>
                        Administrace
                      </Link>
                    </li>
                  )}
                  <li>
                    <button onClick={handleLogout} className="w-full text-left px-4 py-3 bg-red-500/80 rounded-lg hover:bg-red-600 transition-colors font-semibold">
                      Odhlásit se
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Navigation />
        <PendingMatchNotifications />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
          <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
          <Route path="/join-session/:sessionCode?" element={<ProtectedRoute><JoinSession /></ProtectedRoute>} />
          <Route path="/create-match" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
          <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
          <Route path="/matches/:id" element={<ProtectedRoute><Match /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />

          <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
          <Route path="/admin/devices" element={<AdminProtectedRoute><DeviceManagement /></AdminProtectedRoute>} />
          <Route path="/admin/venues" element={<AdminProtectedRoute><VenueManagement /></AdminProtectedRoute>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
